import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Shield, Eye, FileText, UserCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Datenschutz = () => {
  const { lang } = useLanguage();
  return (
    <div className="min-h-screen bg-[#0A1628]" data-testid="datenschutz-page">
      {/* Header */}
      <header className="w-full px-6 py-4 border-b border-[#1A2B42]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
              alt="xchannels.ai" 
              className="h-10 sm:h-12"
            />
          </Link>
          <Link 
            to="/login" 
            className="text-[#00D9C8] hover:text-[#00D9C8]/80 flex items-center gap-2 text-sm"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'de' ? 'Zurück' : 'Back'}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">
            Datenschutzerklärung
          </h1>
          <p className="text-[#5A6C7D] mb-8">Letzte Aktualisierung: Dezember 2025</p>

          {/* 1. Verantwortlicher */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-[#00D9C8]" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                1. Verantwortlicher
              </h2>
            </div>
            
            <div className="bg-[#1A2B42] rounded-xl p-6">
              <p className="text-[#A0AEC0] mb-4">
                Verantwortlicher für die Datenverarbeitung auf dieser Website ist:
              </p>
              <div className="space-y-2">
                <p className="text-white font-medium">Dr. Isabell Claus</p>
                <p className="text-[#A0AEC0]">
                  E-Mail: <a href="mailto:hello@xchannels.ai" className="text-[#00D9C8] hover:underline">hello@xchannels.ai</a>
                </p>
              </div>
            </div>
          </section>

          {/* 2. Erhebung und Verarbeitung */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#00D9C8]" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                2. Erhebung und Verarbeitung personenbezogener Daten
              </h2>
            </div>
            
            <div className="bg-[#1A2B42] rounded-xl p-6 space-y-4">
              <p className="text-[#A0AEC0] leading-relaxed">
                Wir erheben und verwenden personenbezogene Daten nur, soweit dies gesetzlich 
                erlaubt ist oder Sie in die Datenerhebung einwilligen.
              </p>
              
              <div className="pt-4 border-t border-[#0A1628]">
                <h3 className="text-[#00D9C8] font-medium mb-3">2.1 Beim Besuch unserer Website</h3>
                <p className="text-[#A0AEC0] leading-relaxed">
                  Beim Aufrufen unserer Website werden durch den auf Ihrem Endgerät zum Einsatz 
                  kommenden Browser automatisch Informationen an den Server unserer Website gesendet. 
                  Diese Informationen werden temporär in einem sogenannten Logfile gespeichert.
                </p>
              </div>
            </div>
          </section>

          {/* 3. Cookies */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#00D9C8]" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                3. Cookies
              </h2>
            </div>
            
            <div className="bg-[#1A2B42] rounded-xl p-6">
              <p className="text-[#A0AEC0] leading-relaxed">
                Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die auf Ihrem 
                Endgerät gespeichert werden. Sie richten keinen Schaden an und enthalten keine Viren.
              </p>
            </div>
          </section>

          {/* 4. Ihre Rechte */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#00D9C8]" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                4. Ihre Rechte
              </h2>
            </div>
            
            <div className="bg-[#1A2B42] rounded-xl p-6">
              <p className="text-[#A0AEC0] mb-4">
                Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00D9C8]"></div>
                  <span className="text-[#A0AEC0]">Recht auf Auskunft</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00D9C8]"></div>
                  <span className="text-[#A0AEC0]">Recht auf Berichtigung oder Löschung</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00D9C8]"></div>
                  <span className="text-[#A0AEC0]">Recht auf Einschränkung der Verarbeitung</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00D9C8]"></div>
                  <span className="text-[#A0AEC0]">Recht auf Widerspruch gegen die Verarbeitung</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00D9C8]"></div>
                  <span className="text-[#A0AEC0]">Recht auf Datenübertragbarkeit</span>
                </li>
              </ul>
            </div>
          </section>

          {/* 5. Kontakt */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#00D9C8]/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#00D9C8]" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                5. Kontakt
              </h2>
            </div>
            
            <div className="bg-[#1A2B42] rounded-xl p-6">
              <p className="text-[#A0AEC0] leading-relaxed mb-4">
                Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten, 
                bei Auskünften, Berichtigung, Sperrung oder Löschung von Daten sowie Widerruf 
                erteilter Einwilligungen wenden Sie sich bitte an:
              </p>
              <div className="space-y-2">
                <p className="text-white font-medium">Dr. Isabell Claus</p>
                <p className="text-[#A0AEC0]">
                  E-Mail: <a href="mailto:hello@xchannels.ai" className="text-[#00D9C8] hover:underline">hello@xchannels.ai</a>
                </p>
              </div>
            </div>
          </section>

          {/* Footer Links */}
          <div className="pt-8 border-t border-[#1A2B42] flex flex-wrap gap-4 text-sm">
            <Link to="/impressum" className="text-[#A0AEC0] hover:text-[#00D9C8]">
              {lang === 'de' ? 'Impressum' : 'Legal Notice'}
            </Link>
            <span className="text-[#5A6C7D]">|</span>
            <Link to="/login" className="text-[#A0AEC0] hover:text-[#00D9C8]">
              {lang === 'de' ? 'Anmelden' : 'Sign in'}
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Datenschutz;
